<template>
  <div class="main">
    <div class="project_sidebar">
      <Loader type="saidbar" />
    </div>

    <div class="main-content" style="width: 100%;">
      <Loader type="main" />
    </div>

    <div class="onboard-chat" v-if="get_data_loading">
      <div class="onboard-chat__title">
        {{ $t('onboard.title') }}
        <button @click="skip">{{ $t('onboard.buttonSkip') }}</button>
      </div>
      <div
        class="onboard-chat__background"
        :style="{ backgroundImage: `url(${chatBackgroundUrl})` }"
      >
        <!-- Передаём схемку сообщений, как и раньше -->
        <Comments type="chat" :onboard="true" :messages="messages" />
        <AddComment
          type="chat"
          :onboard="true"
          :blocked="blocked_send_msg"
        />
      </div>
    </div>

    <!-- Скрытый компонент для совместимости, как у тебя -->
    <Comments
      v-else
      type="chat"
      :onboard="true"
      :messages="messages"
      style="display: none;"
    />
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex"
import Comments from '../components/comment/Comments.vue'
import AddComment from '../components/comment/AddComment.vue'

const TYPING_DELAY = 1000
const BETWEEN_MESSAGES_DELAY = 500

export default {
  name: "OnboardLayout",
  components: {
    Comments,
    AddComment,
  },
  data() {
    return {
      blocked_send_msg: false,
      get_data_loading: false,
      onboardStarted: false,
      processedSteps: new Set(),
      currentStepId: null, // тут вместо step-индекса
      data: {
        use: null,
        list_name: null,
        task_name: null,
        email: null,
      },

      // 🔹 Конфигурация шагов онбординга
      onboardingSteps: [
        {
          id: 'greeting',
          type: 'bot',
          messageKey: 'onboard.greeting',
        },
        {
          id: 'usage',
          type: 'choice',
          messageKey: 'onboard.usageOptionsMessage',
          options: [
            { value: 'personal', labelKey: 'onboard.usageOptionPersonal' },
            { value: 'team', labelKey: 'onboard.usageOptionTeam' },
            { value: 'both', labelKey: 'onboard.usageOptionBoth' },
          ],
        },
        {
          id: 'previousExperience',
          type: 'choice',
          messageKey: 'onboard.previousExperienceMessage',
          options: [
            { value: 'trello',    labelKey: 'onboard.previousExperienceOptionTrello' },
            { value: 'todoist',   labelKey: 'onboard.previousExperienceOptionTodoist' },
            { value: 'microsoft', labelKey: 'onboard.previousExperienceOptionMicrosoft' },
            { value: 'ticktick',  labelKey: 'onboard.previousExperienceOptionTickTick' },
            { value: 'other',     labelKey: 'onboard.previousExperienceOptionOther' },
          ],
        },
        {
          id: 'importInfo',
          type: 'bot',
          messageKey: 'onboard.importInfo',
        },
        {
          id: 'createList',
          type: 'input',
          messageKey: 'onboard.createList',
        },
        {
          id: 'firstTask',
          type: 'input',
          messageKey: 'onboard.firstTask',
        },
        {
          id: 'inviteTeammate',
          type: 'input',
          messageKey: 'onboard.inviteTeammate',
        },
        {
          id: 'finalStep',
          type: 'bot',
          messageKey: 'onboard.finalStep',
        },
      ],
    }
  },
  computed: {
    ...mapGetters([
      "getUserData",
      "getChats",
      "getProjects",
    ]),

    // 🔹 URL для фона
    chatBackgroundUrl() {
      // если у тебя Vite/Webpack по-разному — тут можно адаптировать
      return require('@/assets/static/images/tasks_chat_back.svg')
    },

    // 🔹 Соответствие старой структуре messages, но на основе config
    messages() {
      return this.onboardingSteps.map(step => {
        const message = this.$t(step.messageKey)
        const msg = { message }
        if (step.type === 'choice' && step.options) {
          msg.options = step.options.map(o => this.$t(o.labelKey))
        }
        return msg
      })
    },

    currentStep() {
      return this.onboardingSteps.find(s => s.id === this.currentStepId) || null
    },
  },
  mounted() {
    // Обрезка комментария до 160 символов
    this.$root.$on('onboard:comment_check', (comment) => {
      comment = String(comment).slice(0, 160)
      this.$root.$emit('onboard:comment_checked', comment)
    })

    // Когда Comments прогрузились
    this.$root.$on('loadedComments', () => {
      if (this.onboardStarted) return
      this.onboardStarted = true

      setTimeout(() => {
        this.get_data_loading = true
        this.startOnboardingFlow()
      }, 3000)
    })

    // Основной listener на отправку сообщений пользователем
    this.$root.$on('onboardSendMessage', (payload) => {
      console.log(payload)
      // payload может быть строкой (как сейчас) или объектом (если доработаешь AddComment)
      const message = typeof payload === 'string' ? payload : payload.label || ''
      const value = typeof payload === 'object' && payload.value ? payload.value : null

      this.handleIncomingUserMessage(message, value)
    })
  },
  methods: {
    ...mapActions([
      "getData",
      "socket_add_comment",
    ]),

    skip() {
      this.$emit('onboardFinished', null)
      this.$socket.emit('IN_Onboard', { type: 'finish' })
    },

    normalizeText(text) {
      return String(text || '')
        .toLowerCase()          // приводим к нижнему регистру
        .replace(/\s+/g, ' ')   // все виды пробелов/переносов → один пробел
        .trim()                 // убираем пробелы по краям
    },

    getUserReplyAfter(messageKey) {
      const chat = this.getChats.find(c => c._id == this.getUserData.onboard_chat_id)
      if (!chat) return null

      const botText = this.normalizeText(this.$t(messageKey))
      const messages = chat.messages

      // 1. Находим все бот-сообщения этого шага
      const indexes = []
      for (let i = 0; i < messages.length; i++) {
        if (
          messages[i].user_id !== this.getUserData._id && // bot
          this.normalizeText(messages[i].message) === botText
        ) {
          indexes.push(i)
        }
      }

      if (indexes.length === 0) return null

      // 2. Берем последнее появление бота (важно!)
      const botIndex = indexes[indexes.length - 1]

      // 3. Ищем ПЕРВОЕ сообщение пользователя после него
      for (let i = botIndex + 1; i < messages.length; i++) {
        if (messages[i].user_id === this.getUserData._id) {
          return messages[i].message
        }
      }

      return null
    },

    restoreDataFromChat() {
      const chat = this.getChats.find(c => c._id == this.getUserData.onboard_chat_id)
      if (!chat) return

      // -------------------------
      // 1) ВОССТАНОВЛЕНИЕ usage
      // -------------------------

      const usageReply = this.getUserReplyAfter('onboard.usageOptionsMessage')
      if (usageReply) {
        const step = this.onboardingSteps.find(s => s.id === 'usage')
        const opt = step.options.find(o => this.$t(o.labelKey) === usageReply)
        if (opt) {
          this.data.use = opt.value
        }
      }

      // -------------------------
      // 2) list_name (название списка)
      // -------------------------

      const listName = this.getUserReplyAfter('onboard.createList')
      if (listName) {
        this.data.list_name = listName
      }

      // -------------------------
      // 3) task_name (имя задачи)
      // -------------------------

      const taskName = this.getUserReplyAfter('onboard.firstTask')
      if (taskName) {
        this.data.task_name = taskName
      }

      // -------------------------
      // 4) email (если был опыт работы в команде)
      // -------------------------

      const email = this.restoreEmail()
      if (email) {
        this.data.email = email
      }
    },

    restoreEmail() {
      const chat = this.getChats.find(c => c._id == this.getUserData.onboard_chat_id)
      if (!chat) return null

      const messages = chat.messages

      const inviteText = this.normalizeText(this.$t('onboard.inviteTeammate'))

      // 1. Находим ВСЕ индексы сообщения бота "invite teammate"
      const inviteIndexes = []
      for (let i = 0; i < messages.length; i++) {
        const msg = messages[i]
        if (msg.user_id === chat.auth &&
            this.normalizeText(msg.message) === inviteText) {
          inviteIndexes.push(i)
        }
      }

      if (inviteIndexes.length === 0) return null

      // 2. Берём ПОСЛЕДНЕЕ inviteTeammate сообщение
      const lastInviteIndex = inviteIndexes[inviteIndexes.length - 1]

      // 3. Ищем ВСЕ пользовательские сообщения ПОСЛЕ него
      const userReplies = []
      for (let i = lastInviteIndex + 1; i < messages.length; i++) {
        const msg = messages[i]
        if (msg.user_id === this.getUserData._id) {
          userReplies.push(msg.message)
        }
      }

      // 4. Ищем последнее валидное email-сообщение
      for (let i = userReplies.length - 1; i >= 0; i--) {
        if (this.validateEmail(userReplies[i])) {
          return userReplies[i]
        }
      }

      return null
    },

    resumeOnboarding() {
      const chat = this.getChats.find(c => c._id == this.getUserData.onboard_chat_id)
      if (!chat) return null

      const botMsgs = chat.messages.filter(m => m.user_id === chat.auth)

      // Нет сообщений бота — начинаем с самого начала
      if (botMsgs.length === 0) return 'greeting'

      // Находим последний messageKey по тексту
      const lastBotText = botMsgs[botMsgs.length - 1].message

      const lastStep = this.onboardingSteps.find(step =>
        this.$t(step.messageKey) === lastBotText
      )

      return lastStep ? lastStep.id : 'greeting'
    },

    // 🔹 Старт онбординга
    async startOnboardingFlow() {
      // сначала восстановить last step
      const lastStepId = this.resumeOnboarding()
      this.currentStepId = lastStepId

      // восстановить данные
      this.restoreDataFromChat()

      // продолжить онбординг
      switch (lastStepId) {

        case 'greeting':
          await this.sendBotStepWithTyping('greeting', { allowUserInput: false })
          await this.sendBotStepWithTyping('usage', { allowUserInput: true })
          this.blocked_send_msg = true
          break

        case 'usage':
          this.blocked_send_msg = true
          break

        case 'previousExperience':
          this.blocked_send_msg = true
          break

        case 'importInfo':
          await this.sendBotStepWithTyping('createList', { allowUserInput: true })
          break

        case 'createList':
          this.blocked_send_msg = false
          break

        case 'firstTask':
          this.blocked_send_msg = false
          break

        case 'inviteTeammate':
          this.blocked_send_msg = false
          break

        case 'finalStep':
          this.blocked_send_msg = true
          this.$emit('onboardFinished', this.data)
          break
      }
    },

    async playInitialBotMessages() {
      // 1) Приветствие
      await this.sendBotStepWithTyping('greeting', { allowUserInput: false })

      // 2) Вопрос про сценарий использования
      await this.sendBotStepWithTyping('usage', { allowUserInput: true })
    },

    // 🔹 Универсальная отправка "бот печатает → бот отправляет сообщение"
    async sendBotStepWithTyping(stepId, opts = {}) {

      // предотвращаем повторную отправку
      if (this.processedSteps.has(stepId)) return
      this.processedSteps.add(stepId)

      this.currentStepId = stepId
      this.blocked_send_msg = true

      this.typing()
      await this.wait(TYPING_DELAY)

      const text = this.$t(this.onboardingSteps.find(s => s.id === stepId).messageKey)
      this.sendMessage(text)

      await this.wait(BETWEEN_MESSAGES_DELAY)

      if (opts.allowUserInput) {
        this.blocked_send_msg = false
      }
    },

    wait(ms) {
      return new Promise(resolve => setTimeout(resolve, ms))
    },

    typing() {
      this.$root.$emit(
        'onboardTyping',
        {
          202: [{ users: { name: "TaskPal AI" } }]
        },
        202
      )
    },

    // 🔹 Отправка сообщения (бота) в чат через сокет
    sendMessage(message) {
      const chat = this.getChats.find(chat => chat._id == this.getUserData.onboard_chat_id)
      if (!chat) return

      const data = {
        _id: this.$w_decrypt(chat.auth),
        date: new Date(),
        message,
        user_id: chat.auth,
        user_name: 'TaskPal AI',
        files: [],
        reply: false,
        send: false,
        read: false,
        error: false,
        chat_id: this.getUserData.onboard_chat_id,
      }

      this.socket_add_comment({
        data,
        send: false,
        type: 'chat',
      })

      setTimeout(() => {
        this.socket_add_comment({
          data,
          send: true,
          type: 'chat',
        })
      }, 500)
    },

    // 🔹 Обработка входящего сообщения от пользователя
    async handleIncomingUserMessage(rawMessage, explicitValue = null) {
      const chat = this.getChats.find(chat => chat._id == this.getUserData.onboard_chat_id)
      if (!chat) return

      // пометить прочитанным
      const last_msg_id = chat.messages[chat.messages.length - 1]._id
      if (last_msg_id) {
        this.$socket.emit('IN_ReadMessages', this.getUserData.onboard_chat_id, last_msg_id, chat.auth)
      }

      const message = String(rawMessage || '').slice(0, 160)

      // value — это "чистое" значение выбора, если AddComment будет его присылать
      const valueFromOption = explicitValue || this.resolveOptionValueByLabel(message)

      // Машина состояний
      switch (this.currentStepId) {
        case 'usage':
          await this.handleUsageStep(valueFromOption, message)
          break

        case 'previousExperience':
          await this.handlePreviousExperienceStep(valueFromOption, message)
          break

        case 'createList':
          await this.handleCreateListStep(message)
          break

        case 'firstTask':
          await this.handleFirstTaskStep(message)
          break

        case 'inviteTeammate':
          await this.handleInviteTeammateStep(message)
          break

        default:
          // Если currentStepId ещё не выставлен (например, юзер написал раньше времени),
          // можно игнорировать или перезапустить онбординг
          break
      }
    },

    // 🔹 Определить value по тексту опции (fallback для старого поведения)
    resolveOptionValueByLabel(label) {
      if (!this.currentStep || this.currentStep.type !== 'choice' || !this.currentStep.options) return null
      const step = this.currentStep
      const found = step.options.find(opt => this.$t(opt.labelKey) === label)
      return found ? found.value : null
    },

    // === Обработка конкретных шагов ===

    async handleUsageStep(optionValue, rawMessage) {
      // Если нет value (юзер написал руками странный текст) — можно проигнорировать или просто не двигаться дальше
      if (!optionValue) return

      this.data.use = optionValue
      this.blocked_send_msg = true

      // Переход к шагу previousExperience
      await this.sendBotStepWithTyping('previousExperience', { allowUserInput: true })
    },

    async handlePreviousExperienceStep(optionValue, rawMessage) {
      if (!optionValue) return
      this.blocked_send_msg = true
      // Ветка: Todoist или Microsoft → сначала importInfo, потом createList
      if (['todoist', 'microsoft'].includes(optionValue)) {
        await this.sendBotStepWithTyping('importInfo', { allowUserInput: false })
        await this.sendBotStepWithTyping('createList', { allowUserInput: true })
      } else {
        // Trello, TickTick, Other → сразу createList
        await this.sendBotStepWithTyping('createList', { allowUserInput: true })
      }
    },

    async handleCreateListStep(message) {
      this.data.list_name = message
      await this.sendBotStepWithTyping('firstTask', { allowUserInput: true })
    },

    async handleFirstTaskStep(message) {
      this.data.task_name = message

      // Если использование "team" или "both" → пригласить тиммейта
      if (['team', 'both'].includes(this.data.use)) {
        await this.sendBotStepWithTyping('inviteTeammate', { allowUserInput: true })
      } else {
        await this.sendBotStepWithTyping('finalStep', { allowUserInput: false })
         this.$emit('onboardFinished', this.data)
      }
    },

    async handleInviteTeammateStep(message) {
      if (!this.validateEmail(message)) {
        this.typing()
        await this.wait(TYPING_DELAY)
        this.sendMessage('Invalid email. Please check and try again.')
        return
      }

      this.data.email = message
      await this.sendBotStepWithTyping('finalStep', { allowUserInput: false })

      // Здесь можно дернуть событие завершения онборда:
      this.$emit('onboardFinished', this.data)
    },

    validateEmail(email) {
      const re = /[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?/
      return re.test(String(email).toLowerCase())
    },
  }
}
</script>

<style lang="scss">
.onboard-chat {
  position: fixed;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  width: 410px;
  height: 650px !important;
  background-color: rgb(222, 232, 242);
  box-shadow: 0 8px 20px 0px rgba(0, 0, 0, 0.51);
  border-radius: 15px;

  &__title {
    font-weight: 400;
    font-size: 20px;
    background-color: #fff;
    padding: 8px 10px;
    border-top-left-radius: 15px;
    border-top-right-radius: 15px;
    position: relative;
    z-index: 1;
    display: flex;
    justify-content: space-between;

    button {
      border: 0;
      background: none;
      outline: none;
      color: var(--theme-color);
      cursor: pointer;
    }

    &.border-bottom {
      border-bottom: 1px solid #e0e0e0;
    }
  }

  &__background {
    width: 100%;
    height: calc(100% - 40px);
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
    border-radius: 15px;
  }

  .onboard_textarea {
    width: calc(100% - 28px);
    margin: 0;
  }

  .user_typing.onboard_typing {
    width: calc(100% - 38px);
  }

  .add_comment_block {
    border-bottom-left-radius: 15px;
    border-bottom-right-radius: 15px;
  }

  .comments_block {
    padding-top: 15px;
  }

  .comment.comment_guest.no_avatar {
    .comment_text {
      padding: 7px 11px 9px;
    }
  }

  .friends__comments .comment_text__user_name {
    margin-bottom: 2px;
  }

  .comments_block .comment_guest .comment_text__body .block {
    height: 12px;
    display: block;
  }
}
</style>
